<template>
  <v-card outlined class="fill-height">
    <div v-if="!conversation" class="pa-4 grey--text text-center">
      {{ $t('select_conversation') }}
    </div>
    <template v-else>
      <v-card-title class="subtitle-1">
        {{ $tc('customer', 1) }}
      </v-card-title>

      <v-card-text>
        <template v-if="conversation.customer">
          <div class="font-weight-medium">{{ conversation.customer_name }}</div>
          <div class="caption">{{ conversation.contact_phone }}</div>
          <v-btn
            small
            text
            color="primary"
            class="px-0"
            @click="preferencesDialog = true"
          >
            <v-icon left x-small>fa-sliders-h</v-icon>
            {{ $t('communication_preferences') }}
          </v-btn>
        </template>

        <template v-else>
          <v-alert type="warning" dense text class="caption mb-2">
            {{ $t('unregistered_contact') }}
          </v-alert>
          <div class="caption mb-2">{{ conversation.contact_phone }}</div>

          <!-- Cuando el número coincide con más de un cliente el sistema no
               elige: la decisión es de una persona. -->
          <template v-if="candidates.length > 0">
            <div class="caption font-weight-medium">
              {{ $t('matching_customers') }}
            </div>
            <v-list dense>
              <v-list-item
                v-for="option in candidates"
                :key="`candidate-${option.pk}`"
                @click="linkCustomer(option.pk)"
              >
                <v-list-item-content>
                  <v-list-item-title class="body-2">
                    {{ option.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="caption">
                    {{ option.phone }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <i-btn
            v-if="canCreateCustomer"
            :title="$t('create_customer')"
            icon="fa-user-plus"
            color="primary"
            outlined
            block
            @click="customerDialog = true"
          />
        </template>
      </v-card-text>

      <v-divider />

      <!-- Los botones se habilitan a medida que avanza la gestión comercial -->
      <v-card-title class="subtitle-1">{{ $t('action') }}</v-card-title>
      <v-card-text>
        <v-btn
          block
          small
          color="primary"
          class="mb-2"
          :disabled="!canQuote"
          @click="goToQuotation()"
        >
          <v-icon left x-small>fa-file-invoice-dollar</v-icon>
          {{ $t('create_quotation') }}
        </v-btn>

        <v-btn
          block
          small
          outlined
          color="primary"
          class="mb-2"
          :disabled="!canSendQuotation"
          @click="quotationDialog = true"
        >
          <v-icon left x-small>fa-paper-plane</v-icon>
          {{ $t('send_quotation') }}
        </v-btn>

        <!-- El title va en el span porque un botón deshabilitado no recibe
             eventos del mouse y nunca mostraría su propio tooltip. -->
        <span :title="hasOrder ? '' : $t('no_orders_yet')">
          <v-btn
            block
            small
            outlined
            color="primary"
            class="mb-2"
            :disabled="!hasOrder"
            @click="goToOrders()"
          >
            <v-icon left x-small>fa-clipboard-list</v-icon>
            {{ $t('see_orders') }}
          </v-btn>
        </span>

        <v-btn
          block
          small
          outlined
          color="primary"
          class="mb-2"
          :disabled="!canInvoice"
          @click="goToInvoices()"
        >
          <v-icon left x-small>fa-file-invoice</v-icon>
          {{ $tc('invoice', 1) }}
        </v-btn>

        <v-btn
          v-if="canSeeInventory"
          block
          small
          outlined
          color="primary"
          @click="goToInventory()"
        >
          <v-icon left x-small>fa-boxes</v-icon>
          {{ $tc('inventory', 1) }}
        </v-btn>
      </v-card-text>

      <template v-if="links.length > 0">
        <v-divider />
        <v-card-title class="subtitle-1">{{ $t('related') }}</v-card-title>
        <v-card-text>
          <!-- Cada relacionado lleva a su pantalla: es la forma de aprobar
               la cotización sin salir a buscarla. -->
          <div
            v-for="item in links"
            :key="`link-${item.link}`"
            class="d-flex align-center mb-1"
            :class="{ 'link-row': item.order }"
            @click="openLink(item)"
          >
            <v-chip x-small class="mr-2">{{ kindLabel(item.kind) }}</v-chip>
            <span class="body-2">{{ item.code || item.label }}</span>
            <v-spacer />
            <span v-if="item.state" class="caption grey--text">
              {{ stateLabel(item.state) }}
            </span>
            <v-icon v-if="item.order" x-small class="ml-1">
              fa-chevron-right
            </v-icon>
          </div>
        </v-card-text>
      </template>

      <customer-dialog
        v-model="customerDialog"
        :conversation="conversation"
        @created="onCustomerCreated"
      />

      <quotation-dialog
        v-model="quotationDialog"
        :conversation="conversation"
        @sent="onQuotationSent"
      />

      <i-modal
        :title="$t('communication_preferences')"
        :value="preferencesDialog"
        dontSave
        @close="preferencesDialog = false"
      >
        <preferences-card :customer="conversation.customer" />
      </i-modal>
    </template>
  </v-card>
</template>
<script>
import { mapGetters } from 'vuex'
import CustomerDialog from './CustomerDialog.vue'
import QuotationDialog from './QuotationDialog.vue'
import PreferencesCard from './PreferencesCard.vue'
export default {
  name: 'context-panel',
  components: { CustomerDialog, QuotationDialog, PreferencesCard },
  data() {
    return {
      candidates: [],
      links: [],
      customerDialog: false,
      quotationDialog: false,
      preferencesDialog: false
    }
  },
  computed: {
    ...mapGetters({
      conversation: 'whatsapp/getCurrent',
      company: 'company/getCompanyData',
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin'
    }),
    canCreateCustomer() {
      return (
        this.isAdmin ||
        this.getPermissions(['whatsapp.create_customer_from_conversation'])
      )
    },
    // Activo apenas se identifica o se da de alta al cliente
    canQuote() {
      return (
        this.conversation !== null &&
        this.conversation.customer !== null &&
        (this.isAdmin || this.getPermissions(['sales.add_quotation']))
      )
    },
    // Requiere cliente identificado y permiso de envio
    canSendQuotation() {
      return (
        this.conversation !== null &&
        this.conversation.customer !== null &&
        (this.isAdmin || this.getPermissions(['whatsapp.reply_conversation']))
      )
    },
    hasOrder() {
      return this.links.some((m) => m.kind === 'order')
    },
    // Habilitado cuando ya existe una cotización o una orden
    canInvoice() {
      return (
        this.links.some((m) => m.kind === 'quotation' || m.kind === 'order') &&
        (this.isAdmin || this.getPermissions(['invoices.view_invoice']))
      )
    },
    canSeeInventory() {
      return this.isAdmin || this.getPermissions(['product.view_product'])
    }
  },
  methods: {
    /**
     * Etiqueta del tipo de objeto vinculado.
     *
     * El backend manda el nombre del modelo de Django, que no siempre coincide
     * con la clave del diccionario: la cotización es 'quotation' y la clave es
     * 'quote'. Sin el mapeo se imprime la clave cruda en inglés.
     */
    kindLabel(kind) {
      const claves = { quotation: 'quote' }
      return this.$tc(claves[kind] || kind, 1)
    },
    /**
     * Etiqueta del estado del objeto vinculado.
     *
     * Los estados de ventas son claves, no etiquetas. Los que no estén en el
     * diccionario se muestran tal cual antes que como una clave rota.
     */
    stateLabel(state) {
      return this.$te(state) ? this.$tc(state, 1) : state
    },
    async load() {
      if (!this.conversation) {
        this.candidates = []
        this.links = []
        return
      }

      const opt = { params: { company_id: this.company.pk } }

      const linksResponse = await this.$api.whatsapp.links({
        pk: this.conversation.pk,
        opt
      })
      this.links = linksResponse.data

      if (!this.conversation.customer) {
        const response = await this.$api.whatsapp.candidates({
          pk: this.conversation.pk,
          opt
        })
        this.candidates = response.data
      } else {
        this.candidates = []
      }
    },
    async linkCustomer(pk) {
      const response = await this.$api.whatsapp.linkCustomer({
        pk: this.conversation.pk,
        form: { customer: pk },
        opt: { params: { company_id: this.company.pk } }
      })
      this.$store.dispatch('whatsapp/setCurrent', response.data)
      this.$emit('updated')
    },
    /**
     * Abre el objeto relacionado en su pantalla. La cotización y la entrega
     * se abren por su orden, que es lo que espera la vista de ventas.
     */
    openLink(item) {
      if (!item.order) {
        return
      }
      this.$router.push({
        name: 'sale-pk',
        params: { pk: this.codification({ code: item.order }) }
      })
    },
    onQuotationSent() {
      // El envio vincula la cotizacion a la conversacion: se recarga el
      // contexto para que aparezca entre los relacionados.
      this.load()
      this.$emit('updated')
    },
    onCustomerCreated(payload) {
      this.$store.dispatch('whatsapp/setCurrent', payload.conversation)
      this.$emit('updated')
      this.load()
    },
    /**
     * Lleva al cotizador conservando cliente y conversación, para poder
     * volver a vincular la cotización cuando se cree.
     */
    async goToQuotation() {
      // Se usa el mismo mecanismo con el que el listado de clientes lleva un
      // cliente al cotizador, y se recuerda la conversación para poder
      // vincular la cotización cuando se cree.
      const { data: customer } = await this.$api.customer.show({
        pk: this.conversation.customer,
        opt: { params: { company_id: this.company.pk } }
      })

      this.$store.commit('sales/customerToSale', customer)
      this.$store.commit('whatsapp/setConversationToSale', this.conversation.pk)

      this.$router.push({
        name: 'sale-pk',
        params: { pk: this.codification({ code: 'create' }) }
      })
    },
    goToOrders() {
      this.$router.push({
        name: 'sale',
        query: { customer: this.conversation.customer }
      })
    },
    goToInvoices() {
      this.$router.push({
        name: 'invoice',
        query: { customer: this.conversation.customer }
      })
    },
    goToInventory() {
      this.$router.push({ name: 'inventory' })
    }
  },
  watch: {
    conversation: {
      immediate: true,
      handler() {
        this.load()
      }
    }
  }
}
</script>
<style scoped>
.link-row {
  cursor: pointer;
}
/* La ficha del cliente puede ser más alta que la columna cuando hay varias
   cotizaciones vinculadas: scrollea ella y no la página. */
.v-card {
  overflow-y: auto;
}
</style>
